import React, { useEffect } from "react";
import {
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  Radio,
  Space,
  Typography,
  message,
  Tag,
} from "antd";
import dayjs, { Dayjs } from "dayjs";

const { Title, Text } = Typography;

export type HospedeFormItem = {
  nome: string;
  sobrenome: string;
  cpf: string;
  dataNascimento: Dayjs;
};

function onlyDigits(v: string) {
  return (v ?? "").replace(/\D/g, "");
}

function isValidCpfDigits(d: string) {
  return /^\d{11}$/.test(d);
}

type Props = {
  initial?: {
    hospedes: Array<{
    pessoaId?: number;
    nome: string;
    sobrenome: string;
    cpf: string;
    dataNascimento: string;
    titular?: boolean;
  }>;
  } | null;
  loading?: boolean;
  onSubmit: (payload: {
    hospedes: Array<{
      nome: string;
      sobrenome: string;
      cpf: string;
      dataNascimento: string; // YYYY-MM-DD
      titular: boolean;
    }>;
  }) => Promise<void>;
};

export default function StepHospedes({ initial, loading, onSubmit }: Props) {
  const [form] = Form.useForm();

  // 👇 para exibir badge em tempo real
  const titularIndexWatch = Form.useWatch("titularIndex", form);

  useEffect(() => {
    if (!initial?.hospedes?.length) {
      form.setFieldsValue({
        hospedes: [{ nome: "", sobrenome: "", cpf: "", dataNascimento: null }],
        titularIndex: 0,
      });
      return;
    }

    const found = initial.hospedes.findIndex((h) => !!h.titular);
    const idxTitular = found >= 0 ? found : 0;

    form.setFieldsValue({
      hospedes: initial.hospedes.map((h) => ({
        nome: h.nome,
        sobrenome: h.sobrenome ?? "",
        cpf: h.cpf ?? "",
        dataNascimento: h.dataNascimento
  ? dayjs(String(h.dataNascimento).slice(0, 10), "YYYY-MM-DD", true)
  : null,
      })),
      titularIndex: idxTitular,
    });
  }, [initial, form]);

  const handleFinish = async (values: any) => {
    const hospedes: HospedeFormItem[] = values.hospedes ?? [];
    const titularIndex: number = values.titularIndex;

    if (!hospedes.length) {
      message.warning("Adicione ao menos 1 hóspede.");
      return;
    }

    if (typeof titularIndex !== "number" || titularIndex < 0 || titularIndex >= hospedes.length) {
      message.error("Selecione quem será o hóspede titular.");
      return;
    }

    try {
      const payload = {
        hospedes: hospedes.map((h, idx) => {
          const cpfDigits = onlyDigits(h.cpf);

          if (!isValidCpfDigits(cpfDigits)) {
            throw new Error(`CPF inválido no hóspede #${idx + 1}.`);
          }
          if (!h.dataNascimento) {
            throw new Error(`Data de nascimento obrigatória no hóspede #${idx + 1}.`);
          }

          // 👇 crítica extra: não aceita futuro
          if (h.dataNascimento.isAfter(dayjs(), "day")) {
            throw new Error(`Data de nascimento não pode ser futura (hóspede #${idx + 1}).`);
          }

          return {
            nome: (h.nome ?? "").trim(),
            sobrenome: (h.sobrenome ?? "").trim(),
            cpf: cpfDigits,
            dataNascimento: h.dataNascimento.format("YYYY-MM-DD"),
            titular: idx === titularIndex,
          };
          
        }),
      };

      await onSubmit(payload);
    } catch (e: any) {
      message.error(e?.message || "Verifique os dados dos hóspedes.");
    }
  };

  return (
    <Card
      bordered={false}
      style={{ borderRadius: 16, boxShadow: "0 8px 24px rgba(0,0,0,0.08)" }}
      styles={{ body: { padding: 24 } }}
    >
      <Space direction="vertical" size={16} style={{ width: "100%" }}>
        <div>
          <Title level={2} style={{ margin: 0 }}>
            Dados dos hóspedes
          </Title>
          <Text type="secondary" style={{ fontSize: 16 }}>
            Informe os dados para emissão e confirmação da reserva. Selecione quem será o hóspede titular.
          </Text>
        </div>

        <Form form={form} layout="vertical" onFinish={handleFinish}>
          <Form.Item
            name="titularIndex"
            rules={[{ required: true, message: "Selecione o hóspede titular." }]}
            style={{ marginBottom: 0 }}
          >
            <Radio.Group style={{ width: "100%" }}>
              <Form.List name="hospedes">
                {(fields, { add, remove }) => (
                  <Space direction="vertical" size={14} style={{ width: "100%" }}>
                    {fields.map((field, index) => {
                      const isTitular = titularIndexWatch === index;

                      return (
                        <Card
                          key={field.key}
                          size="small"
                          style={{
                            borderRadius: 14,
                            background: "rgba(0,0,0,0.02)",
                            border: isTitular
                              ? "2px solid rgba(22,119,255,0.35)"
                              : "1px solid rgba(0,0,0,0.06)",
                          }}
                          bodyStyle={{ padding: 16 }}
                        >
                          <Space direction="vertical" size={10} style={{ width: "100%" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                              <Space size={10} wrap>
                                <Text strong style={{ fontSize: 15 }}>
                                  Hóspede {index + 1}
                                </Text>

                                {/* ✅ Badge */}
                                {isTitular && (
                                  <Tag color="blue" style={{ borderRadius: 999, marginInlineStart: 0 }}>
                                    Titular
                                  </Tag>
                                )}
                              </Space>

                              <Space size={10}>
                                <Radio value={index}>Titular</Radio>

                                {fields.length > 1 && (
                                  <Button
                                    danger
                                    disabled={loading}
                                    onClick={() => {
                                      const currentTitular = form.getFieldValue("titularIndex");
                                      remove(field.name);

                                      if (currentTitular === index) {
                                        form.setFieldsValue({ titularIndex: 0 });
                                      } else if (typeof currentTitular === "number" && currentTitular > index) {
                                        form.setFieldsValue({ titularIndex: currentTitular - 1 });
                                      }
                                    }}
                                  >
                                    Remover
                                  </Button>
                                )}
                              </Space>
                            </div>

                            <div
                              style={{
                                display: "grid",
                                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                                gap: 12,
                              }}
                            >
                              <Form.Item
                                {...field}
                                name={[field.name, "nome"]}
                                label="Nome"
                                rules={[{ required: true, message: "Informe o nome." }]}
                              >
                                <Input placeholder="Ex: João" disabled={loading} />
                              </Form.Item>

                              <Form.Item
                                {...field}
                                name={[field.name, "sobrenome"]}
                                label="Sobrenome"
                                rules={[{ required: true, message: "Informe o sobrenome." }]}
                              >
                                <Input placeholder="Ex: da Silva" disabled={loading} />
                              </Form.Item>

                              <Form.Item
                                {...field}
                                name={[field.name, "dataNascimento"]}
                                label="Data de nascimento"
                                rules={[
                                  { required: true, message: "Informe a data de nascimento." },
                                  {
                                    validator: (_, v) => {
                                      if (!v) return Promise.resolve();
                                      return dayjs(v).isAfter(dayjs(), "day")
                                        ? Promise.reject(new Error("Data de nascimento não pode ser futura."))
                                        : Promise.resolve();
                                    },
                                  },
                                ]}
                              >
                                <DatePicker
                                  style={{ width: "100%" }}
                                  format="DD/MM/YYYY"
                                  disabled={loading}
                                  disabledDate={(current) =>
                                    !!current && current > dayjs().endOf("day")
                                  }
                                />
                              </Form.Item>

                              <Form.Item
                                {...field}
                                name={[field.name, "cpf"]}
                                label="CPF"
                                rules={[
                                  { required: true, message: "Informe o CPF." },
                                  {
                                    validator: (_, v) => {
                                      const d = onlyDigits(v);
                                      return isValidCpfDigits(d)
                                        ? Promise.resolve()
                                        : Promise.reject(new Error("CPF deve ter 11 dígitos."));
                                    },
                                  },
                                ]}
                              >
                                <Input placeholder="Somente números" disabled={loading} />
                              </Form.Item>
                            </div>
                          </Space>
                        </Card>
                      );
                    })}

                    <Button
                      onClick={() => {
                        add({ nome: "", sobrenome: "", cpf: "", dataNascimento: null });

                        const currentTitular = form.getFieldValue("titularIndex");
                        if (typeof currentTitular !== "number") form.setFieldsValue({ titularIndex: 0 });
                      }}
                      disabled={loading}
                    >
                      + Adicionar hóspede
                    </Button>

                    <Button
                      type="primary"
                      htmlType="submit"
                      size="large"
                      loading={loading}
                      style={{ borderRadius: 12 }}
                    >
                      Salvar e continuar
                    </Button>
                  </Space>
                )}
              </Form.List>
            </Radio.Group>
          </Form.Item>
        </Form>
      </Space>
    </Card>
  );
}